import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { FiMail, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';

interface EmailVerificationProps {
  email: string;
  onBack: () => void;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({ email, onBack }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setIsVerified(true);
        // User is automatically logged in after email confirmation
      }
    });

    // Cleanup listener on unmount
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleResendEmail = async () => {
    setIsChecking(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      alert('Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Failed to resend email. Please try again later.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center dark:bg-dark-bg bg-light-bg">
      <div className="bg-gray-50 dark:bg-dark-surface p-8 rounded-lg shadow-lg border-2 border-gray-300 dark:border-theme dark:shadow-theme/20 w-full max-w-md">
        {!isVerified ? (
          <>
            <div className="flex justify-center mb-6">
              <div className="relative">
                <FiMail size={64} className="dark:text-theme text-theme" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-theme dark:bg-theme rounded-full animate-pulse"></div>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mb-4 text-center dark:text-theme text-theme">
              Verify Your Email
            </h2>
            
            <div className="space-y-4 mb-6">
              <p className="text-center dark:text-gray-300 text-gray-700">
                We've sent a verification link to:
              </p>
              <p className="text-center font-semibold dark:text-white text-gray-900 break-all">
                {email}
              </p>
              <p className="text-center text-sm dark:text-gray-400 text-gray-600">
                Please check your inbox and click the verification link to activate your account.
              </p>
              <p className="text-center text-sm dark:text-gray-400 text-gray-600">
                Once verified, you'll be automatically logged in.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={isChecking}
                className="w-full px-6 py-2 bg-theme text-white rounded-lg hover:shadow-lg hover:shadow-theme/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                <FiRefreshCw size={18} className={isChecking ? 'animate-spin' : ''} />
                {isChecking ? 'Sending...' : 'Resend Verification Email'}
              </button>
              
              <button
                onClick={onBack}
                className="w-full px-6 py-2 border-2 border-gray-300 dark:border-gray-600 dark:text-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all font-medium"
              >
                Back to Login
              </button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-xs dark:text-blue-300 text-blue-800 text-center">
                💡 Tip: Check your spam folder if you don't see the email within a few minutes.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-6">
              <FiCheckCircle size={64} className="text-green-600 dark:text-neon-green animate-bounce" />
            </div>
            
            <h2 className="text-2xl font-bold mb-4 text-center text-green-600 dark:text-neon-green">
              Email Verified!
            </h2>
            
            <p className="text-center text-gray-700 dark:text-gray-300 mb-6">
              Your email has been successfully verified. You're being logged in...
            </p>
            
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-theme dark:border-theme border-t-transparent rounded-full animate-spin"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
